// PR-MAY14-DATE-PARTITION — shared bucketing helper used by every list
// page that benefits from "Today / Yesterday / This week / Older"
// section headers. Keeps the logic in one place so a future calendar
// fix or i18n change only touches this file.

export type Partition = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'older';

const LABELS: Record<Partition, string> = {
  today:      'Today',
  yesterday:  'Yesterday',
  this_week:  'This week',
  this_month: 'This month',
  older:      'Older',
};

export function partitionLabel(p: Partition): string {
  return LABELS[p];
}

/**
 * Bucket a date string (or Date) into a partition relative to "now".
 * - today: same calendar day
 * - yesterday: previous calendar day
 * - this_week: within the last 7 days (excluding today/yesterday)
 * - this_month: within the last 30 days (excluding the above)
 * - older: anything older than 30 days
 *
 * Works off the local-day midnight so a row created at 23:55 yesterday
 * doesn't slide into "today" because of a timezone offset bug.
 */
export function partitionFor(d: Date | string | null | undefined, now: Date = new Date()): Partition {
  if (!d) return 'older';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return 'older';

  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const rowMid = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.round((todayMid - rowMid) / 86_400_000);

  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7)   return 'this_week';
  if (diffDays < 30)  return 'this_month';
  return 'older';
}

/**
 * Format a date for the per-row inline display. Two patterns:
 *   short:  "13 May"      (used inside lists)
 *   ymd:    "2026-05-13"  (used in machine-readable contexts)
 */
export function fmtShort(d: Date | string | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.getDate();
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day} ${months[date.getMonth()]}`;
}

export function fmtYmd(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Group a list of items (each with a date field) into ordered partition
 * sections. Returns an array of { partition, items } in chronological
 * order (today first, older last). Empty partitions are dropped.
 */
export function groupByPartition<T>(
  items: T[],
  getDate: (item: T) => Date | string | null | undefined,
  now: Date = new Date(),
): Array<{ partition: Partition; label: string; items: T[] }> {
  const buckets: Record<Partition, T[]> = {
    today: [], yesterday: [], this_week: [], this_month: [], older: [],
  };
  for (const item of items) buckets[partitionFor(getDate(item), now)].push(item);
  const order: Partition[] = ['today', 'yesterday', 'this_week', 'this_month', 'older'];
  return order
    .filter((p) => buckets[p].length > 0)
    .map((p) => ({ partition: p, label: LABELS[p], items: buckets[p] }));
}
